import { 
  Component, 
  ElementRef, 
  EventEmitter, 
  OnInit, 
  Output,
  ViewChild 
} from '@angular/core';
import { ResultCardComponent } from '../result-card/result-card.component';
import { CareerResultSchema, FilterFormDataSchema, MatchScore } from '../../../schemas/career-matches';
import { ResultFilterComponent } from '../result-filter/result-filter.component';
import { numberWithCommas, sortByColumnName } from '../../../utils/sharedFunctions';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { CareerSearchStore } from '@app/services/signals/career-search-service';
import { updateUserCareerFilterOptions } from '@app/state/actions/careers.action';
import { analytics } from '@app/events/segment/analytics';

@Component({
  selector: 'app-result-page',
  imports: [
    ResultCardComponent,
    ResultFilterComponent,
  ],
  templateUrl: './result-page.component.html',
  styleUrl: './result-page.component.css'
})
export class ResultPageComponent implements OnInit {
  careerMatches: CareerResultSchema[] = [];
  sortByOptions: string[] = ['Salary', 'A - Z'];
  originalMatches: CareerResultSchema[] = [];
  allCareerMatches: CareerResultSchema[] = [];
  itemsPerPage: number = 20;
  currentPage: number = 1;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @Output() bookmarkEmitter: EventEmitter<number> = new EventEmitter<number>();
  @Output() careerSelectEmitter: EventEmitter<string> = new EventEmitter<string>();
  private subscription = new Subscription();
  searchText = "";
  isSorted: boolean = false;
  isFiltered: boolean = false;
  matchScorePriority: Record<MatchScore, number> = {
    h1_plus: 1,
    h1: 2,
    med: 3,
    low: 4
  };
  userFilterFormData!: FilterFormDataSchema;

  constructor(
    private store: Store<{ 
      careerMatches: CareerResultSchema[],
      careerFilter: FilterFormDataSchema 
    }>,
    private searchStore: CareerSearchStore
  ) {}

  ngOnInit() {
    const sub = this.store.select('careerMatches').subscribe(
      value => this.handleResultChange(value)
    );
    this.subscription.add(sub);
    this.searchText = this.searchStore.searchText();
    if (this.searchText) {
      this.filterBasedOnSearch(this.searchText);
    }
    this.store.select('careerFilter').subscribe(
      value => this.filterCareerResults(value, true) 
    )
  }

  handleResultChange(value: CareerResultSchema[]) {
    this.allCareerMatches = value;
    this.originalMatches = value;
    this.updateResults();
  }

  updateResults() {
    if (this.allCareerMatches.length > 0) {
      this.careerMatches = this.allCareerMatches.slice(
        0, 
        this.currentPage * this.itemsPerPage
      );
    } else {
      this.careerMatches = [];
    }
  }

  loadMore() {
    analytics.track('Career Load More', { section: 'explore' });
    const nextPageItems = this.allCareerMatches.slice(
      this.currentPage * this.itemsPerPage,
      (this.currentPage + 1) * this.itemsPerPage
    );
    this.careerMatches = [...this.careerMatches, ...nextPageItems];
    this.currentPage++;
  }

  get hasMoreItems(): boolean {
    return this.careerMatches.length < this.allCareerMatches.length;
  }

  scrollToTop() {
    this.scrollContainer.nativeElement.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }

  toggleBookmark(i: number) {
    this.bookmarkEmitter.emit(i);
  }

  sortMatchResults(option: string) {
    let updatedCareerMatches = structuredClone(
      this.isFiltered ? this.allCareerMatches : this.originalMatches
    );
    if (option !== "") {
      updatedCareerMatches = updatedCareerMatches.map(item => {
        item.salary = this.convertSalaryToNumber(item.salary);
        return item;
      });
      let sortedData = sortByColumnName(updatedCareerMatches, 'salary', 'desc');

      updatedCareerMatches = sortedData.map((item: CareerResultSchema) => {
        item.salary = '$' + numberWithCommas(item.salary);
        return item;
      });
      this.isSorted = true;
    } else {
      updatedCareerMatches = updatedCareerMatches.sort((a, b) => {
        return this.matchScorePriority[a.match_score] - this.matchScorePriority[b.match_score];
      });
      this.isSorted = false;
    }
    this.allCareerMatches = updatedCareerMatches;
    this.updateResults();
  }

  filterCareerResults(formData: FilterFormDataSchema, updateFilter: boolean = false) {
    if (!updateFilter) {
      this.store.dispatch(updateUserCareerFilterOptions({ filter: formData }));
    }
    
    let updatedMatches = structuredClone(this.originalMatches);
    
    // filter on career area
    if (formData.area.length > 0) {
      updatedMatches = updatedMatches.filter(e =>
        e.area.some(area => formData.area.includes(area))
      );
    }

    //check for match score only if user selects atleast one option
    const qualityValues = Object.values(formData.quality);
    if (!qualityValues.every(v => v === true) && !qualityValues.every(v => v === false)) {
      const qualities: string[] = [];
      for (const [key, value] of Object.entries(formData.quality)) {
        if (value === true) {
          qualities.push(key);
        }
      }
      updatedMatches = updatedMatches.filter(e => qualities.includes(e.match_score));
    }
    
    //check for education only if user selects atleast one option
    const eduValues = Object.values(formData.education);
    if (!eduValues.every(v => v === true) && !eduValues.every(v => v === false)) {
      const educations: string[] = [];
      for (const [key, value] of Object.entries(formData.education)) {
        if (value === true) {
          educations.push(key);
        }
      }
      updatedMatches = updatedMatches.filter(e => educations.includes(e.education));
    }

    // filter on salary
    const minSalary = parseInt(formData.salary.min_value + '000');
    const maxSalary = parseInt(formData.salary.max_value + '000');
    updatedMatches = updatedMatches.filter(e => {
      const itemSalary = this.convertSalaryToNumber(e.salary);
      return itemSalary >= minSalary && itemSalary <= maxSalary;
    });

    this.isFiltered = true;
    this.allCareerMatches = updatedMatches;
    if (this.isSorted) {
      this.sortMatchResults('Salary');
    } else {
      this.updateResults();
    }
  }

  convertSalaryToNumber(value: string | number) {
    return parseInt(value.toString().replace('$', '').replace(/,/g, ''));
  }

  onSelectCareer(onetCode: string) {
    // Find the career details for tracking
    const career = this.allCareerMatches.find(c => c.onet_code === onetCode);
    if (career) {
      analytics.track('Career View', {
        section: 'explore',
        onet_identifier: onetCode,
        career: career.title
      });
    }
    this.careerSelectEmitter.emit(onetCode);
  }

  filterBasedOnSearch(text: string) {
    let updatedMatches = structuredClone(this.originalMatches);

    updatedMatches = updatedMatches.filter(item => 
      item.title.toLowerCase().includes(text.toLowerCase())
    );

    this.allCareerMatches = updatedMatches;
    this.careerMatches = updatedMatches;
  }
}
